import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import * as XLSX from 'xlsx';
import Layout from '../../components/Layout';

const HodTeacherAttendance = ({ onNavigate, userInfo }) => {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Attendance data
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Filter states
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'monthly'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Date range for Excel download
  const [fromDate, setFromDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('all'); // This serves as the 'daily' search term or generic
  // Separate states for refined monthly view
  const [monthlyDepartmentFilter, setMonthlyDepartmentFilter] = useState('all');
  const [teacherSearchText, setTeacherSearchText] = useState('');
  const [showTeacherSuggestions, setShowTeacherSuggestions] = useState(false);

  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Statistics
  const [statistics, setStatistics] = useState({
    totalSubmissions: 0, // Used for daily
    pendingReviews: 0,
    approvedToday: 0,
    rejectedToday: 0,
    totalTeachers: 0,
    presentToday: 0,
    absentToday: 0,
    attendanceRate: 0,
    // Monthly stats
    avgAttendance: 0,
    totalWorkingDays: 0
  });

  // Monthly Aggregated Data
  const [monthlyStats, setMonthlyStats] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);





  // Fetch teacher attendance data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log(`🔍 Starting API call to fetch teacher attendance data (${viewMode})...`);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          setError('Authentication token not found. Please login again.');
          return;
        }



        // Fetch teacher attendance data
        console.log(`📊 Fetching attendance for ${viewMode === 'daily' ? selectedDate : `${selectedMonth}/${selectedYear}`}`);

        let url = `${API_LINK}/api/hod/teacher-attendance-detailed`;
        if (viewMode === 'daily') {
          url += `?date=${selectedDate}`;
        } else {
          url += `?type=monthly&month=${selectedMonth}&year=${selectedYear}`;
        }

        const attendanceResponse = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!attendanceResponse.ok) {
          throw new Error(`HTTP error! status: ${attendanceResponse.status}`);
        }

        const attendanceResult = await attendanceResponse.json();

        if (attendanceResult.success) {
          console.log('✅ Teacher attendance data loaded successfully');
          console.log(`📈 Found ${attendanceResult.data.records.length} attendance records`);

          setAttendanceRecords(attendanceResult.data.records);
          setFilteredRecords(attendanceResult.data.records); // Initialize filtered records

          // Handle Statistics
          if (viewMode === 'daily') {
            setStatistics(attendanceResult.data.statistics);
            setMonthlyStats([]); // Clear monthly stats
          } else {
            // FOR MONTHLY: Aggregate records on client side if API returns raw list
            // Assuming API returns array of ALL logs for that month
            const records = attendanceResult.data.records;
            const uniqueTeachers = [...new Set(records.map(r => r.teacher_id))];

            const statsByTeacher = uniqueTeachers.map(id => {
              const teacherRecords = records.filter(r => r.teacher_id === id);
              const presentCount = teacherRecords.filter(r => r.attendance_status === 'Present').length;
              const absentCount = teacherRecords.filter(r => r.attendance_status === 'Absent').length;
              const total = teacherRecords.length; // Or days in month? Using recorded days for now.
              const teacherInfo = teacherRecords[0]; // Grab basic info from first record

              return {
                teacher_id: id,
                teacher_name: teacherInfo.teacher_name,
                employee_id: teacherInfo.employee_id,
                department: teacherInfo.department,
                designation: teacherInfo.designation,
                present: presentCount,
                absent: absentCount,
                total_days: total, // Days recorded
                percentage: total > 0 ? ((presentCount / total) * 100).toFixed(1) : 0
              };
            });

            setMonthlyStats(statsByTeacher);
            setFilteredRecords(statsByTeacher); // Reuse filteredRecords for table display

            // Calculate aggregate monthly stats for cards
            const totalPresents = statsByTeacher.reduce((sum, t) => sum + t.present, 0);
            const totalAbsents = statsByTeacher.reduce((sum, t) => sum + t.absent, 0);
            const totalRecorded = statsByTeacher.reduce((sum, t) => sum + t.total_days, 0);

            setStatistics({
              ...statistics, // Keep existing structure just in case
              totalTeachers: uniqueTeachers.length,
              avgAttendance: totalRecorded > 0 ? ((totalPresents / totalRecorded) * 100).toFixed(1) : 0,
              totalWorkingDays: 30 // Approximate or calc from unique dates
            });
          }

        } else {
          throw new Error(attendanceResult.message || 'Failed to fetch attendance data');
        }

      } catch (err) {
        console.error('💥 Error loading teacher attendance data:', err);
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedDate, selectedMonth, selectedYear, viewMode]); // Dependencies updated

  // Filter and sort records
  useEffect(() => {
    let filtered = viewMode === 'daily' ? [...attendanceRecords] : [...monthlyStats];

    // Apply status filter (Daily only vs Monthly logic?)
    if (statusFilter !== 'all') {
      if (viewMode === 'daily') {
        if (statusFilter === 'present') {
          filtered = filtered.filter(record => record.attendance_status === 'Present');
        } else if (statusFilter === 'absent') {
          filtered = filtered.filter(record => record.attendance_status === 'Absent');
        } else {
          filtered = filtered.filter(record => record.status === statusFilter);
        }
      } else {
        // Monthly: Filter by teachers who have mostly Present/Absent? Or skip status filter?
        // For now, let's say status filter might not apply cleanly to monthly summary,
        // or we could filter by "Has Absents" etc. Let's disable status filter for monthly or keep it simple.
        // Keeping it simple: maybe just filter by who was present/absent TODAY? No, that doesn't make sense.
        // Let's ignore status filter for monthly for now or implement "Perfect Attendance" later.
      }
    }

    // Apply department filter
    if (viewMode === 'daily') {
      if (departmentFilter !== 'all') {
        filtered = filtered.filter(record => record.department === departmentFilter);
      }
      // Apply search filter for daily
      if (searchTerm) {
        filtered = filtered.filter(record =>
          record.teacher_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (record.teacher_id && record.teacher_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (record.employee_id && record.employee_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
          record.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.designation.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
    } else {
      // Monthly View Filters
      if (monthlyDepartmentFilter !== 'all') {
        filtered = filtered.filter(stats => stats.department === monthlyDepartmentFilter);
      }

      if (teacherSearchText) {
        filtered = filtered.filter(stats =>
          stats.teacher_name.toLowerCase().includes(teacherSearchText.toLowerCase())
        );
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          if (viewMode === 'daily') return new Date(b.created_at) - new Date(a.created_at);
          return 0; // No date concept for monthly summary rows
        case 'oldest':
          if (viewMode === 'daily') return new Date(a.created_at) - new Date(b.created_at);
          return 0;
        case 'teacher':
          return a.teacher_name.localeCompare(b.teacher_name);
        case 'department':
          return a.department.localeCompare(b.department);
        case 'status':
          if (viewMode === 'daily') return (a.status || '').localeCompare(b.status || '');
          return b.percentage - a.percentage; // for monthly, maybe sort by %?
        case 'hours':
          if (viewMode === 'daily') return (b.total_hours || 0) - (a.total_hours || 0);
          return 0;
        default:
          return 0;
      }
    });

    setFilteredRecords(filtered);
    setCurrentPage(1);
  }, [attendanceRecords, monthlyStats, statusFilter, searchTerm, departmentFilter, monthlyDepartmentFilter, teacherSearchText, sortBy, viewMode]);



  // Handle attendance action (for basic status updates)
  const handleAttendanceAction = async (recordId, action, reviewerName = 'HOD') => {
    try {
      setActionLoading(true);
      setActionError(null);
      setActionSuccess(null);

      console.log(`🔄 Processing ${action} for record ${recordId}`);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API_LINK}/api/hod/teacher-attendance/${recordId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: action,
          notes: `Status updated by HOD`
        })
      });

      const result = await response.json();

      if (result.success) {
        console.log(`✅ Successfully ${action} record ${recordId}`);

        // Update local state
        setAttendanceRecords(prev => prev.map(record =>
          record.id === recordId ? {
            ...record,
            status: action,
            reviewed_by: reviewerName,
            reviewed_at: new Date().toISOString()
          } : record
        ));

        if (selectedRecord && selectedRecord.id === recordId) {
          setSelectedRecord({
            ...selectedRecord,
            status: action,
            reviewed_by: reviewerName,
            reviewed_at: new Date().toISOString()
          });
        }

        setActionSuccess(result.message);

        setTimeout(() => setActionSuccess(null), 3000);
      } else {
        setActionError(result.message);
      }

    } catch (error) {
      console.error('💥 Error updating attendance:', error);
      setActionError(`Failed to perform action: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };



  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate duration between check-in and check-out
  const calculateDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 'N/A';
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diff = end - start;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Download Excel function
  const downloadExcel = async () => {
    try {
      console.log(`📥 Downloading Excel for date range: ${fromDate} to ${toDate}`);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        alert('Authentication token not found. Please login again.');
        return;
      }

      // Fetch data for the date range
      const url = `${API_LINK}/api/hod/teacher-attendance-detailed?type=range&fromDate=${fromDate}&toDate=${toDate}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data.records.length > 0) {
        const records = result.data.records;

        // Aggregate data by teacher
        const uniqueTeachers = [...new Set(records.map(r => r.teacher_id))];

        const excelData = uniqueTeachers.map(id => {
          const teacherRecords = records.filter(r => r.teacher_id === id);
          const presentCount = teacherRecords.filter(r => r.attendance_status === 'Present').length;
          const absentCount = teacherRecords.filter(r => r.attendance_status === 'Absent').length;
          const totalDays = teacherRecords.length;
          const attendancePercentage = totalDays > 0 ? ((presentCount / totalDays) * 100).toFixed(2) : '0.00';

          // Calculate total working hours - ensure it's always a valid number
          const totalHours = teacherRecords.reduce((sum, r) => {
            const hours = parseFloat(r.total_hours) || 0;
            return sum + hours;
          }, 0);

          const teacherInfo = teacherRecords[0];

          return {
            'Employee ID': teacherInfo.employee_id || 'N/A',
            'Teacher Name': teacherInfo.teacher_name || 'N/A',
            'Department': teacherInfo.department || 'N/A',
            'Designation': teacherInfo.designation || 'N/A',
            'Email': teacherInfo.teacher_email || 'N/A',
            'Total Days': totalDays,
            'Present Days': presentCount,
            'Absent Days': absentCount,
            'Attendance %': attendancePercentage,
            'Total Working Hours': totalHours.toFixed(2)
          };
        });

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        ws['!cols'] = [
          { wch: 15 }, // Employee ID
          { wch: 25 }, // Teacher Name
          { wch: 30 }, // Department
          { wch: 20 }, // Designation
          { wch: 30 }, // Email
          { wch: 12 }, // Total Days
          { wch: 12 }, // Present Days
          { wch: 12 }, // Absent Days
          { wch: 15 }, // Attendance %
          { wch: 20 }  // Total Working Hours
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Teacher Attendance');

        // Generate filename with date range
        const filename = `Teacher_Attendance_${fromDate}_to_${toDate}.xlsx`;

        // Download the file
        XLSX.writeFile(wb, filename);

        console.log('✅ Excel file downloaded successfully');
        alert(`Excel file downloaded successfully!\nRecords: ${excelData.length} teachers`);
      } else {
        alert('No attendance records found for the selected date range.');
      }
    } catch (error) {
      console.error('💥 Error downloading Excel:', error);
      alert(`Failed to download Excel: ${error.message}`);
    }
  };

  // Get status info
  const getStatusInfo = (record) => {
    if (record.attendance_status === 'Present') {
      return { color: '#28a745', bg: '#d4edda', icon: '✅', text: 'Present' };
    } else if (record.attendance_status === 'Absent') {
      return { color: '#dc3545', bg: '#f8d7da', icon: '❌', text: 'Absent' };
    } else {
      return { color: '#ffc107', bg: '#fff3cd', icon: '⏳', text: 'Pending' };
    }
  };

  // Get unique departments for filter
  const getUniqueDepartments = () => {
    const departments = [...new Set(attendanceRecords.map(record => record.department))];
    return departments.sort();
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);

  return (
    <Layout role="hod" onNavigate={onNavigate} userInfo={userInfo} activePage="hod-teacher-attendance">
      {({ hodData, isMobile, handleLogout }) => (
        <>
          {/* Modal CSS */}
          <style>
            {`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            padding: 1rem;
            box-sizing: border-box;
          }
          
          .modal-content {
            background: white;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
            max-width: 900px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            position: relative;
          }
        `}
          </style>

          {/* Loading Component */}
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e3f2fd',
                borderTop: '4px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Teacher Attendance...</p>
            </div>
          )}

          {/* Error Component */}
          {error && !loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem'
            }}>
              <div style={{
                backgroundColor: '#ffffff',
                padding: '3rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                textAlign: 'center',
                maxWidth: '400px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ fontSize: '4rem', color: '#ff6b6b', marginBottom: '1.5rem' }}>⚠️</div>
                <h3 style={{ color: '#333', margin: '0 0 1rem 0', fontSize: '1.4rem', fontWeight: 600 }}>Unable to Load Teacher Attendance</h3>
                <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem', lineHeight: 1.5 }}>{error}</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => window.location.reload()} style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Try Again</button>
                  <button onClick={handleLogout} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Logout</button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && (
            <>
              {/* Page Header */}
              <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                borderLeft: '4px solid #238548'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <div>
                    <h2 style={{
                      color: '#238548',
                      margin: '0 0 0.5rem 0',
                      fontSize: '1.8rem',
                      fontWeight: 'bold'
                    }}>
                      👨‍🏫 Teacher Attendance Management
                    </h2>
                    <p style={{
                      color: '#666',
                      margin: 0,
                      fontSize: '1rem'
                    }}>
                      Monitor and manage faculty attendance for {hodData.department}
                    </p>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{
                      background: '#f8f9fa',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <strong>Total: {statistics.totalTeachers}</strong>
                    </div>

                    <div style={{
                      background: '#d4edda',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #c3e6cb'
                    }}>
                      Present: {statistics.presentToday}
                    </div>

                    <div style={{
                      background: '#f8d7da',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #f5c6cb'
                    }}>
                      Absent: {statistics.absentToday}
                    </div>

                    <div style={{
                      background: '#d1ecf1',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #bee5eb'
                    }}>
                      Rate: {statistics.attendanceRate}%
                    </div>
                  </div>
                </div>

                {/* Excel Download Section */}
                <div style={{
                  background: '#fff3cd',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #ffeaa7',
                  marginTop: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    flex: 1
                  }}>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.25rem',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        📅 From Date
                      </label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        max={toDate}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.25rem',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        📅 To Date
                      </label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        min={fromDate}
                        max={new Date().toISOString().split('T')[0]}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  </div>

                  <button
                    onClick={downloadExcel}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#238548',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#1e7139';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(35, 133, 72, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#238548';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    📥 Download Excel
                  </button>
                </div>
              </div>

              {/* Statistics Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
              }}>
                {[
                  {
                    icon: '👥',
                    title: 'Total Faculty',
                    value: statistics.totalTeachers,
                    subtitle: 'In your department',
                    color: '#238548'
                  },
                  {
                    icon: viewMode === 'daily' ? '✅' : '📈',
                    title: viewMode === 'daily' ? 'Present Today' : 'Avg Attendance',
                    value: viewMode === 'daily' ? statistics.presentToday : `${statistics.avgAttendance}%`,
                    subtitle: viewMode === 'daily' ? `${statistics.attendanceRate}% attendance` : 'Monthly Average',
                    color: '#28a745'
                  },
                  {
                    icon: '❌',
                    title: viewMode === 'daily' ? 'Absent Today' : 'Total Absences',
                    value: viewMode === 'daily' ? statistics.absentToday : (monthlyStats.reduce((sum, t) => sum + t.absent, 0) || 0),
                    subtitle: viewMode === 'daily' ? 'Not marked present' : 'This Month',
                    color: '#dc3545'
                  },
                  {
                    icon: '⏰',
                    title: 'Average Hours',
                    value: statistics.totalTeachers > 0 ?
                      (attendanceRecords.reduce((sum, r) => sum + (r.total_hours || 0), 0) / (statistics.totalTeachers || 1)).toFixed(1) : '0',
                    subtitle: 'Hours per teacher',
                    color: '#17a2b8'
                  }
                ].map((stat, index) => (
                  <div key={index} style={{
                    background: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    }}>
                    <div style={{
                      fontSize: '2rem',
                      color: stat.color,
                      marginBottom: '0.5rem'
                    }}>
                      {stat.icon}
                    </div>
                    <h3 style={{ margin: '0 0 0.25rem 0', color: '#333', fontSize: '0.9rem' }}>{stat.title}</h3>
                    <p style={{
                      fontSize: '1.8rem',
                      fontWeight: 'bold',
                      color: stat.color,
                      margin: '0.25rem 0'
                    }}>
                      {stat.value}
                    </p>
                    <p style={{
                      color: '#666',
                      margin: 0,
                      fontSize: '0.8rem'
                    }}>
                      {stat.subtitle}
                    </p>
                  </div>
                ))}
              </div>

              {/* Action Messages */}
              {(actionSuccess || actionError) && (
                <div style={{
                  background: actionSuccess ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${actionSuccess ? '#c3e6cb' : '#f5c6cb'}`,
                  color: actionSuccess ? '#155724' : '#721c24',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>
                    {actionSuccess ? '✅' : '❌'}
                  </span>
                  {actionSuccess || actionError}
                </div>
              )}

              {/* Filters and Search */}
              <div style={{
                background: '#ffffff',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem'
              }}>
                {/* Filters Header and Mode Toggle */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1.5rem',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  borderBottom: '1px solid #eee',
                  paddingBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', gap: '0.5rem', background: '#f8f9fa', padding: '0.25rem', borderRadius: '8px' }}>
                    <button
                      onClick={() => setViewMode('daily')}
                      style={{
                        padding: '0.5rem 1.5rem',
                        borderRadius: '6px',
                        border: 'none',
                        background: viewMode === 'daily' ? '#238548' : 'transparent',
                        color: viewMode === 'daily' ? '#fff' : '#666',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Daily View
                    </button>
                    <button
                      onClick={() => setViewMode('monthly')}
                      style={{
                        padding: '0.5rem 1.5rem',
                        borderRadius: '6px',
                        border: 'none',
                        background: viewMode === 'monthly' ? '#238548' : 'transparent',
                        color: viewMode === 'monthly' ? '#fff' : '#666',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Monthly View
                    </button>
                  </div>

                  {/* Dynamic Date Selectors - REMOVED from here as moved to filter row */}
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    {viewMode === 'daily' && (
                      <div>
                        <input
                          type="date"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          style={{
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #ddd',
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {viewMode === 'daily' ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    alignItems: 'end'
                  }}>
                    {/* Search */}
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        🔍 Search
                      </label>
                      <input
                        type="text"
                        placeholder="Search by teacher, department..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          transition: 'border-color 0.3s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#238548';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e9ecef';
                        }}
                      />
                    </div>

                    {/* Status Filter - Daily Only */}
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        Status Filter
                      </label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          backgroundColor: '#fff'
                        }}
                      >
                        <option value="all">All Status</option>
                        <option value="present">Present</option>
                        <option value="absent">Absent</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>

                    {/* Department Filter - Daily */}
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        Department Filter
                      </label>
                      <select
                        value={departmentFilter}
                        onChange={(e) => setDepartmentFilter(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          backgroundColor: '#fff'
                        }}
                      >
                        <option value="all">All Departments</option>
                        {getUniqueDepartments().map(dept => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </select>
                    </div>

                    {/* Sort By */}
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        Sort By
                      </label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          backgroundColor: '#fff'
                        }}
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="teacher">Teacher Name</option>
                        <option value="department">Department</option>
                        <option value="status">Status</option>
                        <option value="hours">Working Hours</option>
                      </select>
                    </div>

                    {/* Clear Filters */}
                    <div>
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setStatusFilter('all');
                          setDepartmentFilter('all');
                          setSortBy('newest');
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: '#f8f9fa',
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          fontWeight: '500',
                          color: '#333',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#e9ecef';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#f8f9fa';
                        }}
                      >
                        🗑️ Clear Filters
                      </button>
                    </div>

                  </div>
                ) : (
                  // MONTHLY VIEW FILTERS - 3 Column Layout
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    gap: '1.5rem',
                    alignItems: 'end'
                  }}>
                    {/* 1. Department Filter */}
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        Department
                      </label>
                      <select
                        value={monthlyDepartmentFilter}
                        onChange={(e) => setMonthlyDepartmentFilter(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          backgroundColor: '#fff',
                          height: '45px'
                        }}
                      >
                        <option value="all">All Departments</option>
                        {getUniqueDepartments().map(dept => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </select>
                    </div>

                    {/* 2. Month & Year Selector */}
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        Select Month & Year
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <select
                          value={selectedMonth}
                          onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                          style={{
                            flex: 2,
                            padding: '0.75rem',
                            borderRadius: '8px',
                            border: '2px solid #e9ecef',
                            fontSize: '0.95rem',
                            height: '45px'
                          }}
                        >
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {new Date(0, i).toLocaleString('default', { month: 'long' })}
                            </option>
                          ))}
                        </select>
                        <select
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            borderRadius: '8px',
                            border: '2px solid #e9ecef',
                            fontSize: '0.95rem',
                            height: '45px'
                          }}
                        >
                          {Array.from({ length: 5 }, (_, i) => (
                            <option key={i} value={new Date().getFullYear() - 2 + i}>
                              {new Date().getFullYear() - 2 + i}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* 3. Teacher Search with Suggestions */}
                    <div style={{ position: 'relative' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        Search Teacher
                      </label>
                      <input
                        type="text"
                        placeholder="Type teacher name..."
                        value={teacherSearchText}
                        onChange={(e) => {
                          setTeacherSearchText(e.target.value);
                          setShowTeacherSuggestions(e.target.value.length > 0);
                        }}
                        onFocus={() => setShowTeacherSuggestions(teacherSearchText.length > 0)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          height: '45px',
                          transition: 'border-color 0.3s ease'
                        }}
                      />
                      {/* Suggestions Dropdown */}
                      {showTeacherSuggestions && teacherSearchText && (
                        <ul style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: '#fff',
                          borderRadius: '0 0 8px 8px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          border: '1px solid #eee',
                          borderTop: 'none',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          margin: 0,
                          padding: 0,
                          listStyle: 'none',
                          zIndex: 10
                        }}>
                          {(() => {
                            // Use monthlyStats as source of unique teachers (since it has 1 entry per teacher)
                            // or filter unique teachers from current loaded data
                            const sourceData = monthlyStats.length > 0 ? monthlyStats : attendanceRecords;
                            // Extract unique teacher names
                            const uniqueNames = [...new Set(sourceData.map(r => r.teacher_name))]
                              .filter(name => name.toLowerCase().includes(teacherSearchText.toLowerCase()));

                            if (uniqueNames.length === 0) return <li style={{ padding: '0.75rem', color: '#999' }}>No matches found</li>;

                            return uniqueNames.map((name, idx) => (
                              <li
                                key={idx}
                                onClick={() => {
                                  setTeacherSearchText(name);
                                  setShowTeacherSuggestions(false);
                                }}
                                style={{
                                  padding: '0.75rem',
                                  cursor: 'pointer',
                                  borderBottom: idx < uniqueNames.length - 1 ? '1px solid #eee' : 'none'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                                onMouseLeave={(e) => e.target.style.background = '#fff'}
                              >
                                {name}
                              </li>
                            ));
                          })()}
                        </ul>
                      )}
                      {/* Click outside listener could be added to close suggestions, 
                             for now simplistic show/hide on selection logic is used */}
                      {(showTeacherSuggestions && teacherSearchText) && <div style={{ position: 'fixed', inset: 0, zIndex: 5 }} onClick={() => setShowTeacherSuggestions(false)}></div>}
                    </div>
                  </div>
                )}
              </div>

              {/* Teacher Attendance Records List */}
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                marginBottom: '2rem'
              }}>
                {viewMode === 'monthly' ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                          <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Teacher</th>
                          <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Department</th>
                          <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#28a745' }}>Present</th>
                          <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#dc3545' }}>Absent</th>
                          <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#17a2b8' }}>Total Days</th>
                          <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#495057' }}>Attendance %</th>
                          {/* <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#495057' }}>Action</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((record, index) => (
                          <tr key={record.teacher_id} style={{ borderBottom: '1px solid #ecf0f1', background: index % 2 === 0 ? '#fff' : '#fcfcfc' }}>
                            <td style={{ padding: '1rem' }}>
                              <div style={{ fontWeight: '600' }}>{record.teacher_name}</div>
                              <div style={{ fontSize: '0.85rem', color: '#666' }}>{record.employee_id}</div>
                            </td>
                            <td style={{ padding: '1rem' }}>{record.department}</td>
                            <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#28a745' }}>{record.present}</td>
                            <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#dc3545' }}>{record.absent}</td>
                            <td style={{ padding: '1rem', textAlign: 'center' }}>{record.total_days}</td>
                            <td style={{ padding: '1rem', textAlign: 'center' }}>
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                background: parseFloat(record.percentage) >= 75 ? '#d4edda' : parseFloat(record.percentage) >= 50 ? '#fff3cd' : '#f8d7da',
                                color: parseFloat(record.percentage) >= 75 ? '#155724' : parseFloat(record.percentage) >= 50 ? '#856404' : '#721c24'
                              }}>
                                {record.percentage}%
                              </span>
                            </td>
                            {/* <td style={{ padding: '1rem', textAlign: 'center' }}>
                                        <button style={{ padding: '0.5rem', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>View</button>
                                    </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  currentRecords.length > 0 ? (
                    currentRecords.map((record, index) => {
                      const statusInfo = getStatusInfo(record);

                      return (
                        <div
                          key={record.id}
                          style={{
                            padding: '1.5rem',
                            borderBottom: index < currentRecords.length - 1 ? '1px solid #ecf0f1' : 'none',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#ffffff'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ffffff';
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                            marginBottom: '1rem',
                            flexWrap: 'wrap',
                            gap: '1rem'
                          }}>
                            <div style={{ flex: 1, minWidth: '300px' }}>
                              <h3 style={{
                                margin: '0 0 0.5rem 0',
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                color: '#333'
                              }}>
                                {record.teacher_name} ({record.employee_id})
                              </h3>
                              <p style={{
                                margin: '0 0 0.5rem 0',
                                color: '#666',
                                fontSize: '0.9rem'
                              }}>
                                <strong>{record.designation}</strong> • {record.department}
                              </p>
                              <p style={{
                                margin: '0 0 0.5rem 0',
                                color: '#777',
                                fontSize: '0.85rem'
                              }}>
                                📍 {record.location_name}
                              </p>
                              <p style={{
                                margin: 0,
                                color: '#777',
                                fontSize: '0.85rem'
                              }}>
                                📧 {record.teacher_email}
                              </p>
                            </div>

                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                              gap: '0.5rem',
                              minWidth: '200px'
                            }}>
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                borderRadius: '15px',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                backgroundColor: statusInfo.bg,
                                color: statusInfo.color,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}>
                                {statusInfo.icon} {statusInfo.text}
                              </span>

                              <div style={{
                                fontSize: '0.8rem',
                                color: '#999',
                                textAlign: 'right'
                              }}>
                                <div>⏰ In: {formatTime(record.check_in_time)}</div>
                                <div>⏰ Out: {formatTime(record.check_out_time)}</div>
                                <div>📅 {formatDate(record.created_at)}</div>
                                {record.total_hours > 0 && (
                                  <div style={{ fontWeight: 'bold', color: '#238548' }}>
                                    ⏱️ {record.total_hours}h
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div style={{
                            display: 'flex',
                            gap: '0.5rem',
                            alignItems: 'center',
                            flexWrap: 'wrap'
                          }}>
                            <button
                              onClick={() => {
                                setSelectedRecord(record);
                                setShowDetailModal(true);
                                setActionError(null);
                                setActionSuccess(null);
                              }}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#17a2b8',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '500'
                              }}
                            >
                              👁️ View Details
                            </button>

                            {record.attendance_status === 'Absent' && (
                              <button
                                onClick={() => handleAttendanceAction(record.id, 'approved')}
                                disabled={actionLoading}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#28a745',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: '500',
                                  opacity: actionLoading ? 0.7 : 1
                                }}
                              >
                                ✅ Mark Present
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{
                      padding: '3rem',
                      textAlign: 'center',
                      color: '#666'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                      <h3 style={{ margin: '0 0 1rem 0' }}>No Teacher Records Found</h3>
                      <p style={{ margin: 0 }}>
                        {searchTerm || statusFilter !== 'all' || departmentFilter !== 'all'
                          ? 'Try adjusting your search filters'
                          : 'No teacher records available for the selected date'
                        }
                      </p>
                    </div>
                  )
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #238548',
                      borderRadius: '8px',
                      background: currentPage === 1 ? '#f8f9fa' : '#238548',
                      color: currentPage === 1 ? '#6c757d' : '#ffffff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    ← Previous
                  </button>

                  <span style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #238548',
                      borderRadius: '8px',
                      background: currentPage === totalPages ? '#f8f9fa' : '#238548',
                      color: currentPage === totalPages ? '#6c757d' : '#ffffff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}


              {/* Detail Modal */}
              {showDetailModal && selectedRecord && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    {/* Modal Header */}
                    <div style={{
                      background: '#238548',
                      color: '#ffffff',
                      padding: '1.5rem 2rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 'bold' }}>
                        👨‍🏫 Teacher Details
                      </h2>
                      <button
                        onClick={() => setShowDetailModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ffffff',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          borderRadius: '4px',
                          lineHeight: 1
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Modal Content */}
                    <div style={{ padding: '2rem' }}>
                      {/* Teacher Information */}
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        border: '1px solid #e9ecef'
                      }}>
                        <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Teacher Information</h3>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                          gap: '1rem'
                        }}>
                          <div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Name:</strong> {selectedRecord.teacher_name}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Employee ID:</strong> {selectedRecord.employee_id}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Email:</strong> {selectedRecord.teacher_email}
                            </div>
                          </div>
                          <div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Department:</strong> {selectedRecord.department}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Designation:</strong> {selectedRecord.designation}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Location:</strong> {selectedRecord.location_name}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Attendance Details */}
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        border: '1px solid #e9ecef'
                      }}>
                        <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Attendance Details</h3>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                          gap: '1rem'
                        }}>
                          <div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Status:</strong>
                              <span style={{
                                marginLeft: '0.5rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                backgroundColor: getStatusInfo(selectedRecord).bg,
                                color: getStatusInfo(selectedRecord).color
                              }}>
                                {getStatusInfo(selectedRecord).icon} {getStatusInfo(selectedRecord).text}
                              </span>
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Check-in:</strong> {formatTime(selectedRecord.check_in_time)}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Check-out:</strong> {formatTime(selectedRecord.check_out_time)}
                            </div>
                          </div>
                          <div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Date:</strong> {formatDate(selectedRecord.created_at)}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Total Hours:</strong> {selectedRecord.total_hours || 0}h
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Location Verified:</strong> {selectedRecord.location_verified ? 'Yes' : 'No'}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Quick Actions */}
                      {selectedRecord.attendance_status === 'Absent' && (
                        <div style={{
                          background: '#fff3cd',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          border: '1px solid #ffeaa7',
                          textAlign: 'center'
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Quick Actions</h3>
                          <button
                            onClick={() => {
                              handleAttendanceAction(selectedRecord.id, 'approved', hodData?.name);
                              setShowDetailModal(false);
                            }}
                            disabled={actionLoading}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: '#28a745',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              opacity: actionLoading ? 0.7 : 1
                            }}
                          >
                            {actionLoading ? '⏳ Processing...' : '✅ Mark as Present'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </>
          )}
        </>
      )}
    </Layout>
  );
};

export default HodTeacherAttendance;